import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import styles from "./MainLayout.module.scss";
import Header from "../Header/Header";
import SideBar from "../SideBar/SideBar";
import { useSelector } from "react-redux";
import PageLoader from "../../components/Loader/PageLoader";
import LogoutModal from "../../modals/LogoutModal";
import OrganizationSwitchModal from "../../modals/OrganizationSwitchModal";
import {
  CONFLUENCE_TREE_LOADER,
  ORGANIZATION_LOADER,
  PAGE_LOADER,
  REFRESH_BUTTON_LOADER,
} from "../../constants/loader";
import ErrorModal from "../../modals/ErrorModal";
import FloatingBar from "../../components/FloatingBar/FloatingBar";
import NewFindingModal from "../../modals/NewFindingModal";
import NewImprovementProjectModal from "../../modals/NewImprovementProjectModal";
import ExportModal from "../../modals/ExportModal";
import { useMediaQuery } from "@mui/material";
import LoaderOverlay from "../../components/LoaderOverlay/LoaderOverlay";
import ScreenshotButton from "../../components/ScreenshotButton/ScreenshotButton";
import { Outlet } from "react-router-dom";

const LayoutContext = createContext(null);

const MainLayout = ({ floatingBarCollapsedByDefault = false, ...props }) => {
  const parentLayout = useContext(LayoutContext);
  const isNested = Boolean(parentLayout);

  const [floatingBarDefault, setFloatingBarDefault] = useState(
    floatingBarCollapsedByDefault,
  );
  const loadingContexts = useSelector((state) => state.loader.loadingContexts);
  const floatingBarVisible = useSelector(
    (state) => state.layout.floatingBarVisible,
  );

  const isConfluenceTreeLoading = loadingContexts[CONFLUENCE_TREE_LOADER];
  const isRefreshDataLoading = loadingContexts[REFRESH_BUTTON_LOADER];
  const isPageLoading = loadingContexts[PAGE_LOADER];
  const isOrganizationLoading = loadingContexts[ORGANIZATION_LOADER];

  useEffect(() => {
    setFloatingBarDefault(floatingBarCollapsedByDefault);
  }, [floatingBarCollapsedByDefault]);

  useEffect(() => {
    if (!isNested) return undefined;

    parentLayout?.setFloatingBarDefault?.(floatingBarCollapsedByDefault);
    return () => parentLayout?.setFloatingBarDefault?.(false);
  }, [isNested, parentLayout, floatingBarCollapsedByDefault]);

  const isMobile = useMediaQuery("(max-width:768px)");
  const content = useMemo(() => props.children ?? <Outlet />, [props.children]);

  if (isNested) {
    return content;
  }

  const showContentLoader = isPageLoading;
  const showTreeLoader = isConfluenceTreeLoading;
  const showBlockingOverlay =
    (isRefreshDataLoading || isOrganizationLoading) &&
    !showContentLoader &&
    !showTreeLoader;
  const showContentOverlay = showContentLoader || showTreeLoader;
  const overlayTranslationKey = showContentOverlay
    ? showTreeLoader
      ? "pageLoader.readyShortly"
      : "pageLoader.gatheringInfo"
    : "pageLoader.readyShortly";

  const contentClassName = [
    styles.content,
    showBlockingOverlay || showContentOverlay ? styles.contentWithOverlay : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <LayoutContext.Provider
      value={{ inLayout: true, setFloatingBarDefault: setFloatingBarDefault }}
    >
      <Header />
      <LogoutModal />
      <OrganizationSwitchModal />
      <ErrorModal />
      <NewFindingModal />
      <NewImprovementProjectModal />
      <ExportModal />
      <LoaderOverlay />
      <SideBar />
      <div className={styles.main}>
        <div className={contentClassName}>{content}</div>
        {(showBlockingOverlay || showContentOverlay) && (
          <div className={styles.loaderOverlay}>
            <PageLoader translationKey={overlayTranslationKey} />
          </div>
        )}
      </div>
      {/* {!isMobile && floatingBarVisible && (
        <FloatingBar collapsedByDefault={floatingBarDefault} />
      )} */}
      {/* {!isMobile && (
        <div data-ignore-outside-click={true}>
          <ScreenshotButton />
        </div>
      )} */}
    </LayoutContext.Provider>
  );
};

export default MainLayout;
